import * as React from 'react';
import { cn } from '@noor/lib/cn';

/**
 * The three moderation-state glyphs the merchant panel badges categories,
 * branches and products with, taken verbatim from the Noor.Merchant Figma file
 * (nodes 1218:5019 `checkbox-circle-fill`, 1218:5032
 * `icon-park-outline:loading-one`, 1218:5022 `alert-fill`).
 *
 * Like `AlertStatusIcon`, these carry the design's exact tints and do NOT
 * follow `currentColor`: a state's colour IS its meaning here — orange is "in
 * review", green "live", red "sent back" — and letting a parent recolour one
 * would let the sidebar's hover tint turn a rejection green. Size them with
 * `size-*`.
 */
export interface ModerationStatusIconProps extends React.SVGProps<SVGSVGElement> {
  className?: string;
}

/** Orange spinner — «В модерации» / «На модерации», waiting on a moderator. */
export function ModerationPendingIcon({
  className,
  ...props
}: ModerationStatusIconProps) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8 1.33333V2.66667M11.3333 2.22667L10.6667 3.38133M13.7733 4.66667L12.6187 5.33333M14.6667 8H13.3333M13.7733 11.3333L12.6187 10.6667M11.3333 13.7733L10.6667 12.6187M8 14.6667V13.3333M4.66667 13.7733L5.33333 12.6187M2.22667 11.3333L3.38133 10.6667M1.33333 8H2.66667M2.22667 4.66667L3.38133 5.33333M4.66667 2.22667L5.33333 3.38133"
        stroke="#FF6D00"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Green filled check-circle — «Активна» / «Активен», live on the storefront. */
export function ModerationApprovedIcon({
  className,
  ...props
}: ModerationStatusIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10 18.3333C5.3975 18.3333 1.66667 14.6025 1.66667 10C1.66667 5.3975 5.3975 1.66667 10 1.66667C14.6025 1.66667 18.3333 5.3975 18.3333 10C18.3333 14.6025 14.6025 18.3333 10 18.3333ZM9.16917 13.3333L15.0608 7.44083L13.8825 6.2625L9.16917 10.9767L6.81167 8.61917L5.63333 9.7975L9.16917 13.3333Z"
        fill="#16A34A"
      />
    </svg>
  );
}

/** Red filled warning triangle — «Отклонена» / «Отклонено», needs rework. */
export function ModerationRejectedIcon({
  className,
  ...props
}: ModerationStatusIconProps) {
  return (
    <svg
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M10.7217 2.5L18.66 16.25C18.7331 16.3767 18.7716 16.5204 18.7716 16.6667C18.7716 16.8129 18.7331 16.9566 18.66 17.0833C18.5869 17.21 18.4817 17.3152 18.355 17.3883C18.2283 17.4615 18.0846 17.5 17.9383 17.5H2.06167C1.91539 17.5 1.77169 17.4615 1.64501 17.3883C1.51833 17.3152 1.41313 17.21 1.33999 17.0833C1.26686 16.9566 1.22835 16.8129 1.22835 16.6667C1.22836 16.5204 1.26686 16.3767 1.34 16.25L9.27833 2.5C9.35148 2.37333 9.45667 2.26814 9.58335 2.195C9.71003 2.12187 9.85373 2.08337 10 2.08337C10.1463 2.08337 10.29 2.12187 10.4166 2.195C10.5433 2.26814 10.6485 2.37333 10.7217 2.5V2.5ZM9.16667 13.3333V15H10.8333V13.3333H9.16667ZM9.16667 7.5V11.6667H10.8333V7.5H9.16667Z"
        fill="#EF4444"
      />
    </svg>
  );
}

/**
 * Cutlery glyph — the «Загрузить меню» action on the Menu screen (Figma
 * 1224:5251 `ic:round-local-dining`). Unlike the status glyphs above this one
 * DOES follow `currentColor`: it labels an action rather than reporting a
 * state, so it takes the button's own colour.
 */
export function LocalDiningIcon({ className, ...props }: ModerationStatusIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M8.1 13.34L10.93 10.51L4.74 4.33C4.26 3.85 3.43 3.98 3.13 4.6C2.42 6.09 2.68 7.92 3.91 9.16L8.1 13.34ZM14.88 11.53C16.41 12.24 18.56 11.74 20.15 10.15C22.06 8.24 22.43 5.5 20.96 4.03C19.5 2.57 16.76 2.93 14.84 4.84C13.25 6.43 12.75 8.58 13.46 10.11L4.41 19.16C4.22302 19.347 4.11798 19.6006 4.11798 19.865C4.11798 20.1294 4.22302 20.383 4.41 20.57C4.59698 20.757 4.85057 20.862 5.115 20.862C5.37943 20.862 5.63302 20.757 5.82 20.57L12 14.41L18.18 20.59C18.2726 20.6826 18.3825 20.756 18.5035 20.8061C18.6244 20.8562 18.7541 20.882 18.885 20.882C19.0159 20.882 19.1456 20.8562 19.2665 20.8061C19.3875 20.756 19.4974 20.6826 19.59 20.59C19.6826 20.4974 19.756 20.3875 19.8061 20.2665C19.8562 20.1456 19.882 20.0159 19.882 19.885C19.882 19.7541 19.8562 19.6244 19.8061 19.5035C19.756 19.3825 19.6826 19.2726 19.59 19.18L13.41 13L14.88 11.53Z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * Storefront glyph — the «Загрузить ресторан» action on the Venue screen
 * (Figma 1224:5279 `store-2-fill`). Follows `currentColor` for the same reason
 * as {@link LocalDiningIcon}.
 */
export function StoreFrontIcon({ className, ...props }: ModerationStatusIconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className={cn('shrink-0', className)}
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M22 20V22H2V20H3V13.242C2.38437 12.8311 1.87971 12.2745 1.53082 11.6218C1.18193 10.969 0.999592 10.2402 1 9.5C1 8.673 1.224 7.876 1.633 7.197L4.345 2.5C4.43277 2.34798 4.559 2.22175 4.71101 2.13398C4.86303 2.04621 5.03547 2 5.211 2H18.79C18.9655 2 19.138 2.04621 19.29 2.13398C19.442 2.22175 19.5682 2.34798 19.656 2.5L22.358 7.182C22.9546 8.17287 23.1463 9.35553 22.8934 10.4841C22.6405 11.6127 21.9624 12.6005 21 13.242V20H22ZM5.789 4L3.356 8.213C3.11958 8.79714 3.11248 9.44903 3.33613 10.0382C3.55978 10.6273 3.99768 11.1103 4.56218 11.3904C5.12668 11.6705 5.77614 11.7271 6.38058 11.5488C6.98502 11.3706 7.49984 10.9706 7.822 10.429C8.157 9.592 9.342 9.592 9.678 10.429C9.8633 10.8934 10.1836 11.2916 10.5975 11.5721C11.0115 11.8526 11.5 12.0025 12 12.0025C12.5 12.0025 12.9885 11.8526 13.4025 11.5721C13.8164 11.2916 14.1367 10.8934 14.322 10.429C14.657 9.592 15.842 9.592 16.178 10.429C16.3078 10.7484 16.5022 11.0376 16.7491 11.2783C16.996 11.519 17.2901 11.706 17.6127 11.8275C17.9354 11.9491 18.2797 12.0026 18.6241 11.9847C18.9684 11.9668 19.3053 11.8778 19.6136 11.7234C19.9219 11.569 20.1949 11.3525 20.4155 11.0875C20.6361 10.8225 20.7995 10.5148 20.8955 10.1836C20.9914 9.85238 21.0178 9.50493 20.973 9.16305C20.9281 8.82118 20.8131 8.49227 20.635 8.197L18.21 4H5.79H5.789Z"
        fill="currentColor"
      />
    </svg>
  );
}
